import React, { useState, useEffect } from 'react';
import { Delete } from 'lucide-react';
import Modal from '@/components/shared/Modal/Modal';
import { playClickSound } from '@/utils/soundUtils';

const QUICK_ADD_AMOUNTS = [10, 20, 50, 100, 200, 500, 1000];

export interface QuickAmountModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialAmount: number;
  onConfirm: (amount: number) => void;
  maxAmount?: number;
  /** Optional title, e.g. game name */
  title?: string;
}

/**
 * Modal for quick amount entry: numpad 0-9 and quick add buttons (10, 20, 50, 100, 200, 500, 1000).
 * User can type digits or tap quick add to set/add amount, then confirm.
 */
const QuickAmountModal: React.FC<QuickAmountModalProps> = ({
  isOpen,
  onClose,
  initialAmount,
  onConfirm,
  maxAmount = 99999,
  title,
}) => {
  const [value, setValue] = useState<string>(String(Math.max(0, Math.round(initialAmount))));

  useEffect(() => {
    if (isOpen) {
      setValue(String(Math.max(0, Math.round(initialAmount))));
    }
  }, [isOpen, initialAmount]);

  const num = parseInt(value, 10) || 0;
  const displayNum = Math.min(num, maxAmount);
  const clampedValue = Math.max(0, Math.min(displayNum, maxAmount));

  const appendDigit = (d: number) => {
    playClickSound();
    const next = value === '0' ? String(d) : value + String(d);
    const nextNum = parseInt(next, 10);
    if (nextNum <= maxAmount) {
      setValue(next);
    }
  };

  const backspace = () => {
    playClickSound();
    if (value.length <= 1) {
      setValue('0');
    } else {
      setValue(value.slice(0, -1));
    }
  };

  const quickAdd = (amount: number) => {
    playClickSound();
    const next = Math.min(maxAmount, (parseInt(value, 10) || 0) + amount);
    setValue(String(next));
  };

  const handleConfirm = () => {
    playClickSound();
    onConfirm(clampedValue);
    onClose();
  };

  const handleClear = () => {
    playClickSound();
    setValue('0');
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title ?? 'Enter amount'}
      size="sm"
      closeOnOverlayClick={true}
      className="origin-center scale-90 sm:scale-100"
      footer={
        <div className="flex items-center justify-between w-full gap-3">
          <button
            type="button"
            onClick={handleClear}
            className="px-4 py-2 rounded-lg border border-white/20 text-white/80 hover:bg-white/10 transition-colors text-sm font-semibold"
          >
            Clear
          </button>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-white/20 text-white/80 hover:bg-white/10 transition-colors text-sm font-semibold"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              className="px-5 py-2 rounded-lg bg-[#FB923C] text-white font-bold hover:opacity-90 transition-opacity text-sm"
            >
              Confirm
            </button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Display */}
        <div
          className="rounded-lg border border-white/20 bg-black/30 px-4 py-3 text-right"
          style={{ fontFamily: '"JetBrains Mono", monospace' }}
        >
          <span className="text-2xl sm:text-3xl font-extrabold text-white">${displayNum}</span>
        </div>

        {/* Quick add */}
        <div>
          <p className="text-white/60 text-xs font-semibold uppercase tracking-wide mb-2">Quick add</p>
          <div className="flex flex-wrap gap-2">
            {QUICK_ADD_AMOUNTS.map((amt) => (
              <button
                key={amt}
                type="button"
                onClick={() => quickAdd(amt)}
                className="px-3 py-2 rounded-lg border border-white/20 bg-white/5 text-white font-bold text-sm hover:bg-white/15 active:scale-95 transition-all"
              >
                +{amt}
              </button>
            ))}
          </div>
        </div>

        {/* Numpad 0-9 */}
        <div>
          <p className="text-white/60 text-xs font-semibold uppercase tracking-wide mb-2">Enter amount</p>
          <div className="grid grid-cols-3 gap-2">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => appendDigit(d)}
                className="h-12 rounded-lg border border-white/20 bg-white/5 text-white font-bold text-lg hover:bg-white/15 active:scale-95 transition-all"
              >
                {d}
              </button>
            ))}
            <button
              type="button"
              onClick={backspace}
              className="h-12 rounded-lg border border-white/20 bg-white/5 text-white hover:bg-white/15 active:scale-95 transition-all flex items-center justify-center"
              aria-label="Backspace"
            >
              <Delete className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={() => appendDigit(0)}
              className="h-12 rounded-lg border border-white/20 bg-white/5 text-white font-bold text-lg hover:bg-white/15 active:scale-95 transition-all"
            >
              0
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              className="h-12 rounded-lg bg-[#FB923C] text-white font-bold text-sm hover:opacity-90 active:scale-95 transition-all"
            >
              OK
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default QuickAmountModal;
